import React, { useMemo, useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { projectTypes } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { CalendarDays, Clock } from "lucide-react";
import { api } from "../lib/api";

const schema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Valid email required"),
  phone: z.string().min(7, "Phone is required"),
  postcode: z.string().min(2, "Postcode is required"),
  projectType: z.string().min(1, "Select a project type"),
  preferredDate: z.date({ required_error: "Choose a date" }),
  preferredTime: z.string().min(1, "Choose a time"),
  consent: z.boolean().refine((v) => v, { message: "Consent required" }),
});

const timeSlots = ["09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00"];

export const BookingForm = ({ className = "" }) => {
  const [open, setOpen] = useState(false);

  const { register, handleSubmit, setValue, watch, reset, control, formState: { errors, isSubmitting } } =
    useForm({ resolver: zodResolver(schema), defaultValues: { consent: true } });

  const preferredDate = watch("preferredDate");

  const onSubmit = async (data) => {
    const payload = { ...data, submittedAt: new Date().toISOString() };
    try {
      await api.post("/leads", payload);
      toast.success("Thanks! Your home visit request has been received.");
      reset({ consent: true });
    } catch (e) {
      console.error(e);
      toast.error("Could not submit right now. Saved locally; we’ll follow up.");
      try {
        const existing = JSON.parse(localStorage.getItem("booking_requests") || "[]");
        existing.push(payload);
        localStorage.setItem("booking_requests", JSON.stringify(existing));
      } catch {}
    }
  };

  useEffect(() => {
    if (preferredDate && open) setOpen(false);
  }, [preferredDate, open]);

  const minDate = useMemo(() => new Date(new Date().toDateString()), []);

  return (
    <div className={`relative ${className}`}>
      <Toaster richColors position="top-right" />
      <form onSubmit={handleSubmit(onSubmit)} className="rounded-xl border border-white/15 bg-white/10 backdrop-blur-xl p-6 md:p-8 shadow-[0_10px_50px_-20px_rgba(0,0,0,0.4)]">
        <div className="mb-6">
          <h3 className="text-xl font-semibold tracking-tight">Book a Home Visit</h3>
          <p className="text-sm text-muted-foreground">Free, no-obligation consultation at a time that suits you.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" placeholder="Jane Doe" {...register("name")} />
            {errors.name && <p className="text-destructive text-xs mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" placeholder="jane@mail.com" {...register("email")} />
            {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" placeholder="07123 456789" {...register("phone")} />
            {errors.phone && <p className="text-destructive text-xs mt-1">{errors.phone.message}</p>}
          </div>
          <div>
            <Label htmlFor="postcode">Postcode</Label>
            <Input id="postcode" placeholder="SW19" {...register("postcode")} />
            {errors.postcode && <p className="text-destructive text-xs mt-1">{errors.postcode.message}</p>}
          </div>
          <div>
            <Label>Project type</Label>
            <Controller control={control} name="projectType" render={({ field }) => (
              <Select value={field.value} onValueChange={(v) => field.onChange(v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a project" />
                </SelectTrigger>
                <SelectContent>
                  {projectTypes.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )} />
            {errors.projectType && (<p className="text-destructive text-xs mt-1">{errors.projectType.message}</p>)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:col-span-2">
            <div>
              <Label>Preferred date</Label>
              <Controller control={control} name="preferredDate" render={({ field }) => (
                <Popover open={open} onOpenChange={setOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" type="button" className="w-full justify-start">
                      <CalendarDays className="h-4 w-4 mr-2" />
                      {field.value ? format(field.value, "EEE d MMM, yyyy") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" align="start">
                    <Calendar mode="single" selected={field.value} onSelect={(d) => field.onChange(d)} disabled={(date) => (date ? date < minDate : false)} initialFocus />
                  </PopoverContent>
                </Popover>
              )} />
              {errors.preferredDate && (<p className="text-destructive text-xs mt-1">{errors.preferredDate.message}</p>)}
            </div>
            <div>
              <Label>Preferred time</Label>
              <Controller control={control} name="preferredTime" render={({ field }) => (
                <Select value={field.value} onValueChange={(v) => field.onChange(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((t) => (
                      <SelectItem key={t} value={t}><div className="flex items-center gap-2"><Clock className="h-4 w-4" />{t}</div></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )} />
              {errors.preferredTime && (<p className="text-destructive text-xs mt-1">{errors.preferredTime.message}</p>)}
            </div>
          </div>
          <div className="flex items-center gap-2 md:col-span-2 mt-2">
            <Controller control={control} name="consent" render={({ field }) => (
              <Checkbox id="consent" checked={!!field.value} onCheckedChange={(v) => field.onChange(!!v)} />
            )} />
            <Label htmlFor="consent" className="text-sm text-muted-foreground">I agree to be contacted about my enquiry.</Label>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <Button type="submit" disabled={isSubmitting} className="bg-amber-600 hover:bg-amber-700 text-white">Book Consultation</Button>
          <p className="text-xs text-muted-foreground">You won’t be charged. We’ll confirm within 24 hours.</p>
        </div>
      </form>
    </div>
  );
};